const DEFAULT_API_ADMIN_URL = "http://localhost:3001";
const DEFAULT_AUTH_LOGIN_URL = "http://localhost:9002";

/**
 * Origem publica do proprio app.
 *
 * Esta funcao existe para a configuracao do SDK no navegador. A origem vem da
 * propria pagina aberta, portanto acompanha o dominio publico sem depender de
 * variavel de ambiente nem de um novo build.
 */
export function getWebsiteDomain(): string {
  if (typeof window === "undefined") {
    throw new Error("getWebsiteDomain must only be called in the browser");
  }

  return window.location.origin;
}

/**
 * Backend do admin. SO e chamada no servidor — o navegador usa `/api/`, que o
 * route handler em `app/api/[...path]` encaminha para ca.
 *
 * `ADMIN_API_URL` nao leva prefixo NEXT_PUBLIC_ de proposito: com ele o `next
 * build` inlinaria o valor no bundle, congelando o endereco do backend na
 * imagem e publicando-o para todo visitante.
 */
export function getAdminApiDomain(): string {
  return (
    process.env.ADMIN_API_URL ||
    process.env.NEXT_PUBLIC_ADMIN_API_URL ||
    DEFAULT_API_ADMIN_URL
  );
}

/**
 * Escopo dos cookies que o SDK grava pelo proprio JS (`sFrontToken` e cia).
 *
 * Congelada no `next build` por necessidade: quem le este valor e o
 * `SuperTokensReact.init()` do cliente. Precisa concordar com
 * `SESSION_COOKIE_DOMAIN`, que vale para a outra metade da sessao — ver
 * `validateSessionCookieConfig`.
 */
export function getSessionTokenFrontendDomain(): string | undefined {
  return process.env.NEXT_PUBLIC_SESSION_TOKEN_FRONTEND_DOMAIN || undefined;
}

/**
 * Dominio pai sob o qual a sessao vale para todos os subdominios — o que permite
 * web e admin compartilharem o mesmo login.
 *
 * SO e lida no servidor, pelo proxy `/api`, ao reescrever o `Set-Cookie` do
 * backend. Sem prefixo NEXT_PUBLIC_ de proposito: o `next build` congelaria o
 * valor na imagem, e trocar de dominio exigiria rebuild.
 *
 * Deve casar com o `SUPERTOKENS_COOKIEDOMAIN` do backend E com o mesmo valor no
 * app web. Divergencia aqui cria dois cookies de mesmo nome em escopos
 * diferentes — que e justamente o conflito que este valor existe para evitar.
 *
 * Sem valor, o cookie fica host-only: o login continua funcionando, mas nao e
 * compartilhado entre os subdominios.
 */
export function getSessionCookieDomain(): string | undefined {
  return process.env.SESSION_COOKIE_DOMAIN?.trim() || undefined;
}

/**
 * Origem do app web, onde fica o login central. O admin nao tem tela de login
 * propria: `/auth/login` daqui redireciona para la com `redirectTo` absoluto, e o
 * web precisa listar a origem do admin em `AUTH_ALLOWED_REDIRECT_ORIGINS`.
 *
 * SO e lida no servidor (sem NEXT_PUBLIC_), entao trocar de dominio nao exige
 * rebuild. Localmente, use `localhost` nos dois apps — cookies ignoram a porta,
 * e misturar `localhost` com `127.0.0.1` separaria as sessoes.
 */
export function getAuthLoginUrl(): string {
  return (process.env.AUTH_LOGIN_URL?.trim() || DEFAULT_AUTH_LOGIN_URL).replace(
    /\/+$/,
    "",
  );
}

export function getSupertokensConnectionURI(): string {
  return process.env.SUPERTOKENS_CONNECTION_URI ?? "";
}

export function getSupertokensAPIKey(): string {
  return process.env.SUPERTOKENS_API_KEY ?? "";
}

export function getSupertokensAppName(): string {
  return process.env.SUPERTOKENS_APP_NAME ?? "GeoOfficePro Platform";
}
